import React from "react";
import { Box, BoxPanel, Typography } from "@tiny/components";
import AutoAwesome from "@tiny/icons/generated/AutoAwesome";
import UserCheck from "@tiny/icons/generated/UserCheck";
import UserCircle from "@tiny/icons/generated/UserCircle";
import WarningOutline from "@tiny/icons/generated/WarningOutline";
import Clock from "@tiny/icons/generated/Clock";
import Moon from "@tiny/icons/generated/Moon";
import Mail from "@tiny/icons/generated/Mail";
import EmailFastOutline from "@tiny/icons/generated/EmailFastOutline";
import UserMinus from "@tiny/icons/generated/UserMinus";
import FlagOutline from "@tiny/icons/generated/FlagOutline";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import { usePostEngagementScoringAnalyticsQuery } from "@tiny/api";

const LEVEL_CONFIG: Record<string, { Icon: React.ElementType; color: string }> =
  {
    "Super Engaged": { Icon: AutoAwesome, color: "#9c27b0" },
    "Highly Engaged": { Icon: UserCheck, color: "#4caf50" },
    "Moderately Engaged": { Icon: UserCircle, color: "#2196f3" },
    "At-Risk": { Icon: WarningOutline, color: "#ff9800" },
    Lapsed: { Icon: Clock, color: "#9e9e9e" },
    Dormant: { Icon: Moon, color: "#607d8b" },
    "Never Engaged": { Icon: Mail, color: "#9e9e9e" },
    "Never Sent": { Icon: EmailFastOutline, color: "#78909c" },
    Unsubscribed: { Icon: UserMinus, color: "#f44336" },
    "Spam Complainer": { Icon: FlagOutline, color: "#212121" },
  };

interface Props {
  segmentIds?: string[];
  showEmptyState?: boolean;
}

export default function CustomerEngagementLevels({
  segmentIds = [],
  showEmptyState,
}: Props) {
  const { mdUp } = useBreakpoints();

  const { data, isFetching } = usePostEngagementScoringAnalyticsQuery(
    { segmentIds },
    { enabled: segmentIds.length > 0, keepPreviousData: true }
  );

  const allResults = data?.results ?? [];
  const results = allResults.filter((r) => r.customerCount > 0);
  const totalCount = results.reduce((sum, r) => sum + r.customerCount, 0);

  const hasNoData =
    !isFetching && results.length === 0 && segmentIds.length > 0;

  if (segmentIds.length === 0) return null;
  if (!showEmptyState && (isFetching || hasNoData)) return null;

  return (
    <BoxPanel title="Customer Engagement Levels" contentWithPadding>
      {isFetching && allResults.length === 0 ? (
        <Box p={4} display="flex" justifyContent="center">
          <Typography variant="body2" color="grey" tone={600}>
            Loading...
          </Typography>
        </Box>
      ) : hasNoData ? (
        <Box p={4} display="flex" justifyContent="center">
          <Typography variant="body2" color="grey" tone={600}>
            No engagement data available
          </Typography>
        </Box>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns={mdUp ? "repeat(5, 1fr)" : "repeat(2, 1fr)"}
          style={{ gap: 16 }}
        >
          {results.map((item, index) => {
            const config = LEVEL_CONFIG[item.engagementLevel] ?? {
              Icon: Mail,
              color: "#9e9e9e",
            };
            const { Icon, color } = config;
            const barWidth =
              totalCount > 0
                ? `${Math.round((item.customerCount / totalCount) * 100)}%`
                : "0%";

            return (
              <Box
                key={index}
                borderRadius="8px"
                p={2}
                display="flex"
                flexDirection="column"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  mb={1}
                  style={{ gap: 8 }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 6,
                      backgroundColor: `${color}18`,
                      flexShrink: 0,
                    }}
                  >
                    <Icon style={{ color, fontSize: 14 }} />
                  </Box>
                  <Typography
                    variant="label2"
                    color="grey"
                    tone={700}
                    fontWeight="bold"
                  >
                    {item.engagementLevel.toUpperCase()}
                  </Typography>
                </Box>
                <Typography
                  variant="h2"
                  fontWeight="semiBold"
                  color="grey"
                  tone={900}
                >
                  {item.customerCount.toLocaleString()}
                </Typography>
                <Box
                  mt={2}
                  width="100%"
                  height={3}
                  bgcolor="#f0f0f0"
                  borderRadius="2px"
                  position="relative"
                >
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    height="100%"
                    bgcolor={color}
                    width={barWidth}
                    borderRadius="2px"
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </BoxPanel>
  );
}
